import type { CalendarResponse } from '@ai-meeting/shared'
import { apiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'
import { getAccessToken } from './session'

let calendarRequest: {
  ownerToken: string | null
  limit: number | undefined
  promise: Promise<CalendarResponse>
} | null = null

/**
 * Read the current user's Google Meet calendar without creating meetings.
 *
 * @param limit Ask the server for at most this many of the earliest events.
 *   The Home panel shows a handful and does not need the whole week.
 */
export function fetchGoogleCalendar(limit?: number): Promise<CalendarResponse> {
  const ownerToken = getAccessToken()
  // The limit is part of the identity of an in-flight request: Home asks for a
  // few and the Upcoming page asks for all of them, and handing one the other's
  // answer would silently truncate a full listing.
  if (
    !calendarRequest ||
    calendarRequest.ownerToken !== ownerToken ||
    calendarRequest.limit !== limit
  ) {
    const promise = apiClient
      .get<CalendarResponse>(
        limit ? `/api/calendar/google/events?limit=${limit}` : '/api/calendar/google/events'
      )
      .then(({ data }) => data)
      .finally(() => {
        if (calendarRequest?.promise === promise) calendarRequest = null
      })
    calendarRequest = { ownerToken, limit, promise }
  }
  return calendarRequest.promise
}

/**
 * Where to send a user who needs to grant calendar access (BRAI-538).
 *
 * Asked of the server rather than built here, because the URL carries a signed
 * state naming the BrSE account being linked. Connecting used to mean signing
 * in with Google again, which handed a Microsoft-SSO user a Google session and
 * took away the account they were working in — this route links a calendar to
 * the account they already have instead.
 *
 * @returns Google's consent URL, to navigate to.
 * @throws When the server will not start consent — the caller shows its own
 *   "could not connect" message rather than navigating nowhere.
 */
export async function startGoogleCalendarConnect(): Promise<string> {
  try {
    const { data } = await apiClient.get<{ authorizationUrl: string }>(
      '/api/calendar/google/connect'
    )
    return data.authorizationUrl
  } catch (error) {
    throw toApiError(error, getServiceStrings().googleCalendarConsentFailed)
  }
}
